import { createApp } from "vue";
import { useCellSelection } from "@/composables/cellSelection.mjs";
import CellSelectionStatusBar from "@/components/CellSelectionStatusBar.vue";

const EXCLUDED_SELECTORS = [
  "input",
  "textarea",
  "select",
  "button",
  "a",
  "[contenteditable='true']",
  ".calc-input",
];

// Assign a stable uid per <table> without mutating its DOM (uses a WeakMap).
const tableUids = new WeakMap();
let tableUidCounter = 0;
function uidFor(tableEl) {
  let uid = tableUids.get(tableEl);
  if (!uid) {
    uid = `t${++tableUidCounter}`;
    tableUids.set(tableEl, uid);
  }
  return uid;
}

// Observe <tbody> mutations per engaged table; clear selection on row changes.
const observedBodies = new WeakSet();
function watchTableForMutations(tableEl, sel) {
  const tbody = tableEl.querySelector("tbody");
  if (!tbody || observedBodies.has(tbody)) return;
  observedBodies.add(tbody);
  const mo = new MutationObserver(() => sel.clearSelection());
  mo.observe(tbody, { childList: true });
}

function isCellEligible(td) {
  if (!td) return false;
  if (td.hasAttribute("data-no-select")) return false;
  if (td.closest("thead")) return false;
  for (const selector of EXCLUDED_SELECTORS) {
    if (td.querySelector(selector)) return false;
  }
  if (td.children.length === 1 && td.children[0].tagName === "TABLE") return false;
  return true;
}

function cellRecord(td) {
  const tr = td.closest("tr");
  const tbody = td.closest("tbody");
  if (!tr || !tbody) return null;
  const tableEl = tbody.closest("table");
  if (!tableEl) return null;
  const rowIdx = Array.prototype.indexOf.call(tbody.children, tr);
  const colIdx = Array.prototype.indexOf.call(tr.children, td);
  return {
    el: td,
    tableUid: uidFor(tableEl),
    tableEl,
    rowIdx,
    colIdx,
    rawText: td.textContent.trim(),
  };
}

function rangeCells(a, b) {
  if (!a || !b || a.tableEl !== b.tableEl) return [];
  const tbody = a.tableEl.querySelector("tbody");
  if (!tbody) return [];
  const r1 = Math.min(a.rowIdx, b.rowIdx);
  const r2 = Math.max(a.rowIdx, b.rowIdx);
  const c1 = Math.min(a.colIdx, b.colIdx);
  const c2 = Math.max(a.colIdx, b.colIdx);
  const out = [];
  for (let r = r1; r <= r2; r++) {
    const row = tbody.children[r];
    if (!row) continue;
    for (let c = c1; c <= c2; c++) {
      const cell = row.children[c];
      if (!cell || cell.tagName !== "TD") continue;
      if (!isCellEligible(cell)) continue;
      const rec = cellRecord(cell);
      if (rec) out.push(rec);
    }
  }
  return out;
}

export default {
  install(app, { router } = {}) {
    const sel = useCellSelection();

    // Mount singleton status bar into document.body (survives route changes).
    const barRoot = document.createElement("div");
    barRoot.id = "cell-selection-bar-root";
    document.body.appendChild(barRoot);
    createApp(CellSelectionStatusBar).mount(barRoot);

    let mouseDownAt = null; // {x, y} for drag-distance check

    document.addEventListener("mousedown", (ev) => {
      if (ev.button !== 0) return;
      const td = ev.target.closest("td");
      if (!td) return;
      const tableEl = td.closest("table");
      if (!tableEl) return;
      if (!isCellEligible(td)) return;

      const rec = cellRecord(td);
      if (!rec) return;

      mouseDownAt = { x: ev.clientX, y: ev.clientY };
      watchTableForMutations(tableEl, sel);

      if (ev.ctrlKey || ev.metaKey) {
        sel.toggleCell(rec);
        sel.setAnchor(rec);
        sel.state.dragging = false;
        return;
      }

      if (ev.shiftKey) {
        if (sel.state.anchor && sel.state.anchor.tableUid === rec.tableUid) {
          // Extend rectangular range from the existing anchor in the same table.
          const anchorCell = { tableEl, ...sel.state.anchor };
          const originalAnchor = { ...sel.state.anchor };
          sel.replaceWithRange(rangeCells(anchorCell, rec));
          sel.setAnchor(originalAnchor);
        } else {
          // No anchor, or anchor is in a different table — establish a fresh
          // single-cell selection in the current table. Do NOT start a drag
          // because the Shift key suppresses mousemove range updates anyway.
          sel.clearSelection();
          sel.addCell(rec);
          sel.setAnchor(rec);
        }
        sel.state.dragging = false;
        return;
      }

      // Plain click → start a selection + potential drag.
      sel.clearSelection();
      sel.addCell(rec);
      sel.setAnchor(rec);
      sel.state.dragging = true;
      sel.state.dragStart = rec;
    });

    document.addEventListener("mousemove", (ev) => {
      if (!sel.state.dragging) return;
      if (ev.ctrlKey || ev.metaKey || ev.shiftKey) return;
      const td = ev.target.closest("td");
      if (!td) return;
      if (!isCellEligible(td)) return;
      const rec = cellRecord(td);
      if (!rec || !sel.state.dragStart) return;
      if (rec.tableUid !== sel.state.dragStart.tableUid) return;
      sel.replaceWithRange(rangeCells(sel.state.dragStart, rec));
    });

    document.addEventListener("mouseup", (ev) => {
      if (sel.state.dragging) {
        const moved =
          mouseDownAt &&
          (Math.abs(ev.clientX - mouseDownAt.x) > 3 ||
            Math.abs(ev.clientY - mouseDownAt.y) > 3);
        sel.state.wasDragging = !!moved;
      }
      sel.state.dragging = false;
      sel.state.dragStart = null;
      mouseDownAt = null;
    });

    document.addEventListener("click", (ev) => {
      // Consume the drag flag up-front so it cannot leak to a future click.
      const dragJustEnded = sel.state.wasDragging;
      sel.state.wasDragging = false;

      if (ev.target.closest("table")) return;
      if (ev.target.closest(".cell-selection-bar")) return;

      // Don't clear if the preceding mouseup was the end of a drag (drag may
      // have overshot the table bounds).
      if (dragJustEnded) return;

      sel.clearSelection();
    });

    if (router && typeof router.afterEach === "function") {
      router.afterEach(() => sel.clearSelection());
    }
  },
};
